import { createSelector } from 'reselect';
import _ from 'lodash';
import moment from 'moment';
import { iterationEndDate, numOfDaysInIteration } from './helpers';
import { getCurrentIteration } from 'common/utils/utils';
import {
  SEARCH_DATE_FORMAT,
  DAYS_PARAM,
  SHORT_DATE_FORMAT
} from 'common/constants/constants';
import {
  numOfDaysInIterationSelector,
  isIterationsPeriodKeySelectedSelector
} from './ui-state';
import { selectedTeamProject } from './projects';

export const ITERATION_STATES = {
  ACTIVE: 'active',
  CLOSE: 'close'
};

export const ITERATIONS_TYPES = {
  WEEKLY: 'weekly',
  SCRUM: 'scrum',
  KANBAN: 'kanban'
};

export const KANBAN_TYPES = [ITERATIONS_TYPES.WEEKLY, ITERATIONS_TYPES.KANBAN];

export const formatIterationRange = iteration =>
  `${moment(iteration.start_date).format(SHORT_DATE_FORMAT)} - ${moment(
    iteration.end_date
  ).format(SHORT_DATE_FORMAT)}`;

const getIterations = state => state.iterations;
const getUiState = state => state.uiState;

export const iterationsSelector = state => state.iterations || [];

export const iterationsPickerSelector = createSelector(
  getIterations,
  iterations =>
    _(iterations)
      .map(
        ({
          start_date,
          end_date,
          name,
          computedName,
          range,
          isVisible,
          id,
          state,
          type
        }) => ({
          start_date,
          end_date,
          name,
          computedName,
          range,
          isVisible,
          id,
          state,
          type
        })
      )
      .value()
);

export const nonComputedIterationsSelector = createSelector(
  getIterations,
  iterations =>
    _(iterations)
      .filter(iteration => !iteration.isComputed)
      .value()
);

// Next Phase: change to currentPeriodSelector
export const currentIterationSelector = createSelector(
  [
    nonComputedIterationsSelector,
    getUiState,
    isIterationsPeriodKeySelectedSelector,
    selectedTeamProject
  ],
  (
    nonComputedIterations,
    uiState,
    isIterationsPeriodKeySelected,
    selectedTeamProject
  ) => {
    if (!nonComputedIterations.length) {
      return null;
    }

    if (!isIterationsPeriodKeySelected) {
      const cadenceType =
        selectedTeamProject?.metadata?.cadence?.type || 'Kanban';
      const type = cadenceType.toLowerCase();

      return {
        end_date: uiState.dates.to,
        start_date: uiState.dates.from,
        type
      };
    }

    return getCurrentIteration(
      nonComputedIterations,
      uiState.dates.from,
      uiState.dates.to,
      uiState.selectedIterationId
    );
  }
);

export const selectedIterationPeriodSelector = createSelector(
  [getIterations, getUiState],
  (iterations, uiState) => {
    if (!iterations.length) {
      return null;
    }

    return getCurrentIteration(
      iterations,
      uiState.dates.from,
      uiState.dates.to,
      uiState.selectedIterationId
    );
  }
);

export const current5IterationsSelector = createSelector(
  [
    nonComputedIterationsSelector,
    currentIterationSelector,
    isIterationsPeriodKeySelectedSelector
  ],
  (nonComputedIterations, currentIteration, isIterationsPeriodKeySelected) => {
    const latestIteration = nonComputedIterations.filter(i => !i.isComputed)[0];
    const defaultCurrentIteration = !isIterationsPeriodKeySelected
      ? latestIteration
      : currentIteration;

    if (!nonComputedIterations.length || !currentIteration) {
      return [];
    }

    const currentIterationIdx = nonComputedIterations.findIndex(
      iteration => iteration.id === defaultCurrentIteration.id
    );
    const fiveIterationChuncks = _.chunk(nonComputedIterations, 5);

    const lastChunk = _(fiveIterationChuncks).last();

    // Append more iterations to last chunk if needed
    if (lastChunk.length !== 5 && fiveIterationChuncks.length > 1) {
      const numOfIterationsToFillToLastChunk = 5 - lastChunk.length;
      const secondLastIteration = _.nth(
        fiveIterationChuncks,
        fiveIterationChuncks.length - 2
      );
      const iterationsToAdd = secondLastIteration.slice(
        5 - numOfIterationsToFillToLastChunk,
        5
      );
      const normalizedLastChunk = _([...iterationsToAdd, ...lastChunk])
        .orderBy('end_date', 'desc')
        .value();
      fiveIterationChuncks[fiveIterationChuncks.length - 1] =
        normalizedLastChunk;
    }

    const currentChunkIdx = Math.floor(currentIterationIdx / 5);

    return fiveIterationChuncks[currentChunkIdx];
  }
);

export const prevIterationSelector = createSelector(
  [nonComputedIterationsSelector, currentIterationSelector],
  (nonComputedIterations, currentIteration) => {
    if (!nonComputedIterations.length || !currentIteration) {
      return null;
    }

    return _(nonComputedIterations)
      .filter(
        iteration =>
          moment(iteration.end_date) < moment(currentIteration.end_date)
      )
      .orderBy('end_date', 'desc')
      .head();
  }
);

export const datesFromTheBeginningOfPrevIterSelector = createSelector(
  [
    prevIterationSelector,
    currentIterationSelector,
    numOfDaysInIterationSelector,
    getUiState,
    isIterationsPeriodKeySelectedSelector
  ],
  (
    prevIteration,
    currentIteration,
    numOfDaysInIteration,
    uiState,
    isIterationsPeriodKeySelected
  ) => {
    const selectedFrom = uiState.dates.from;
    const selectedTo = uiState.dates.to;

    if (!currentIteration || !isIterationsPeriodKeySelected) {
      return {
        from: selectedFrom,
        to: selectedTo
      };
    }

    return {
      from: prevIteration
        ? prevIteration.start_date
        : currentIteration.start_date,
      to:
        // Get the minimum date between yesterday and the last the day of the iteration
        moment
          .min(
            moment(currentIteration.start_date)
              .add(numOfDaysInIteration, DAYS_PARAM)
              .startOf('day'),
            moment().subtract(1, DAYS_PARAM).startOf('day')
          )
          .format(SEARCH_DATE_FORMAT)
    };
  }
);

export const datesFromSelectedTillNowSelector = createSelector(
  [getUiState],
  uiState => {
    const selectedFrom = uiState.dates.from;
    const selectedTo = uiState.dates.to;
    const today = moment().startOf('day');
    return {
      from: selectedFrom,
      to:
        moment(selectedTo) > today
          ? today.add(-1, DAYS_PARAM).format(SEARCH_DATE_FORMAT)
          : selectedTo
    };
  }
);

export const isRetrospectSelector = createSelector(
  [getIterations, currentIterationSelector],
  (iterations, currentIteration) => {
    return currentIteration && currentIteration.state !== 'active';
  }
);

export const oneBeforeLatestOrLatestIterationSelector = createSelector(
  [nonComputedIterationsSelector],
  nonComputedIterations => nonComputedIterations[1] || nonComputedIterations[0]
);

export const getLatestIterationSelector = createSelector(
  [nonComputedIterationsSelector],
  nonComputedIterations => nonComputedIterations[0]
);

export const getLatestIterationEndDateSelector = createSelector(
  [getLatestIterationSelector],
  ({ dates: { to } = {} }) => {
    const pitDate = moment.min(
      moment().startOf('day'),
      moment(to).startOf('day')
    );
    return pitDate.format(SEARCH_DATE_FORMAT);
  }
);

export const numOfDaysInLatestIterationSelector = createSelector(
  [getLatestIterationSelector],
  (latestIteration = {}) => {
    const { start_date, end_date } = latestIteration;
    const endDate = iterationEndDate(end_date);

    return numOfDaysInIteration(start_date, endDate);
  }
);
